import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DiagnosisState } from '../types';
import { ScoringRubricItem } from '../../types'; // Updated import

const initialState: DiagnosisState = {
    // Framework completeness tracking (simplified)
    completedElements: 0,
    completionRate: 0,
    totalElements: 0,
    frameworkCategoryStatusList: [],
    
    // Scoring rubric (enhanced and preserved)
    scoringRubric: [],
    totalPointsEarned: 0,
    totalPointsPossible: 0,
    scoringCompletionPercentage: 0,
    hasScoring: false,
};

const diagnosisSlice = createSlice({
    name: 'diagnosis',
    initialState,
    reducers: {
        // Framework completeness tracking (simplified from category-based system)
        updateDiagnosisCompleteness: (state, action: PayloadAction<{
            completedElements: number;
            completionRate: number;
            totalElements: number;
            frameworkCategoryStatusList: { category_name: string; is_completed: boolean }[];
        }>) => {
            // Update framework progress tracking
            state.completedElements = action.payload.completedElements;
            state.totalElements = action.payload.totalElements;
            state.completionRate = action.payload.completionRate;
            state.frameworkCategoryStatusList = action.payload.frameworkCategoryStatusList;
        },

        // Initialize scoring rubric for a new case
        initializeScoring: (state, action: PayloadAction<ScoringRubricItem[]>) => {
            // Set up new scoring rubric with all scores at 0 and proper defaults for new fields
            state.scoringRubric = action.payload.map(item => ({
                ...item,
                pointsScored: 0, // Always start at 0
                evaluationNotes: "", // Initialize empty notes
                messages: item.messages || [], // NEW: Preserve existing messages or initialize empty
                categorySpecificInstructions: item.categorySpecificInstructions || "" // NEW: Preserve instructions or default to empty
            }));
            
            // Calculate initial totals
            state.totalPointsPossible = action.payload.reduce((sum, item) => sum + item.totalPoints, 0);
            state.totalPointsEarned = 0;
            state.scoringCompletionPercentage = 0;
            state.hasScoring = action.payload.length > 0;
        },
        
        // Update scoring data from WebSocket events
        updateScoring: (state, action: PayloadAction<ScoringRubricItem[]>) => {
            // Update total possible points if not set (first time)
            if (state.totalPointsPossible === 0) {
                state.totalPointsPossible = action.payload.reduce((sum, item) => sum + item.totalPoints, 0);
            }
            
            // Update the scoring rubric with new data, preserving new fields
            state.scoringRubric = action.payload.map(item => ({
                scoringCategory: item.scoringCategory,
                totalPoints: item.totalPoints,
                pointsScored: item.pointsScored,
                evaluationNotes: item.evaluationNotes,
                messages: item.messages || [], // NEW: Preserve messages array
                categorySpecificInstructions: item.categorySpecificInstructions || "" // NEW: Preserve instructions
            }));
            
            // Recalculate earned points and completion percentage
            state.totalPointsEarned = action.payload.reduce((sum, item) => sum + item.pointsScored, 0);
            state.scoringCompletionPercentage = state.totalPointsPossible > 0 
                ? Math.round((state.totalPointsEarned / state.totalPointsPossible) * 100)
                : 0;
            
            // Mark as having scoring if we have rubric items
            state.hasScoring = action.payload.length > 0;
        },

        // Update individual scoring category
        updateScoringCategory: (state, action: PayloadAction<{
            categoryName: string;
            pointsScored: number;
            evaluationNotes: string;
            messages?: string[]; // NEW: Optional messages array
            categorySpecificInstructions?: string; // NEW: Optional instructions
        }>) => {
            const { categoryName, pointsScored, evaluationNotes, messages, categorySpecificInstructions } = action.payload;
            
            const categoryIndex = state.scoringRubric.findIndex(
                item => item.scoringCategory === categoryName
            );
            
            if (categoryIndex !== -1) {
                // Update the specific category with new fields
                state.scoringRubric[categoryIndex].pointsScored = pointsScored;
                state.scoringRubric[categoryIndex].evaluationNotes = evaluationNotes;
                
                // NEW: Update messages if provided
                if (messages !== undefined) {
                    state.scoringRubric[categoryIndex].messages = messages;
                }
                
                // NEW: Update instructions if provided
                if (categorySpecificInstructions !== undefined) {
                    state.scoringRubric[categoryIndex].categorySpecificInstructions = categorySpecificInstructions;
                }
                
                // Recalculate totals
                state.totalPointsEarned = state.scoringRubric.reduce((sum, item) => sum + item.pointsScored, 0);
                state.scoringCompletionPercentage = state.totalPointsPossible > 0 
                    ? Math.round((state.totalPointsEarned / state.totalPointsPossible) * 100)
                    : 0;
            }
        },

        // Add points to a specific category (incremental scoring)
        addPointsToCategory: (state, action: PayloadAction<{
            categoryName: string;
            pointsToAdd: number;
            additionalNotes?: string;
            newMessages?: string[]; // NEW: Messages to append
        }>) => {
            const { categoryName, pointsToAdd, additionalNotes, newMessages } = action.payload;
            
            const categoryIndex = state.scoringRubric.findIndex(
                item => item.scoringCategory === categoryName
            );
            
            if (categoryIndex !== -1) {
                const category = state.scoringRubric[categoryIndex];
                
                // Add points (but don't exceed total possible)
                category.pointsScored = Math.min(
                    category.pointsScored + pointsToAdd,
                    category.totalPoints
                );
                
                // Append additional notes if provided
                if (additionalNotes) {
                    category.evaluationNotes = category.evaluationNotes 
                        ? `${category.evaluationNotes} ${additionalNotes}`
                        : additionalNotes;
                }
                
                // NEW: Append new messages if provided (avoid duplicates)
                if (newMessages && newMessages.length > 0) {
                    const existingMessages = category.messages || [];
                    const uniqueNewMessages = newMessages.filter(msg => !existingMessages.includes(msg));
                    category.messages = [...existingMessages, ...uniqueNewMessages];
                }
                
                // Recalculate totals
                state.totalPointsEarned = state.scoringRubric.reduce((sum, item) => sum + item.pointsScored, 0);
                state.scoringCompletionPercentage = state.totalPointsPossible > 0 
                    ? Math.round((state.totalPointsEarned / state.totalPointsPossible) * 100)
                    : 0;
            }
        },

        // Add message to a specific category without changing points
        addMessageToCategory: (state, action: PayloadAction<{
            categoryName: string;
            message: string;
        }>) => {
            const { categoryName, message } = action.payload;
            
            const categoryIndex = state.scoringRubric.findIndex(
                item => item.scoringCategory === categoryName
            );
            
            if (categoryIndex !== -1) {
                const category = state.scoringRubric[categoryIndex];
                const existingMessages = category.messages || [];
                
                // Add message if it's not already present
                if (!existingMessages.includes(message)) {
                    category.messages = [...existingMessages, message];
                }
            }
        },

        // Update category instructions
        updateCategoryInstructions: (state, action: PayloadAction<{
            categoryName: string;
            instructions: string;
        }>) => {
            const { categoryName, instructions } = action.payload;
            
            const categoryIndex = state.scoringRubric.findIndex(
                item => item.scoringCategory === categoryName
            );
            
            if (categoryIndex !== -1) {
                state.scoringRubric[categoryIndex].categorySpecificInstructions = instructions;
            }
        },

        // Clear all scoring data (for new session)
        clearScoring: (state) => {
            state.scoringRubric = [];
            state.totalPointsEarned = 0;
            state.totalPointsPossible = 0;
            state.scoringCompletionPercentage = 0;
            state.hasScoring = false;
        },

        // Clear framework completeness data (for new session)
        clearDiagnosisCompleteness: (state) => {
            state.completedElements = 0;
            state.totalElements = 0;
            state.completionRate = 0;
            state.frameworkCategoryStatusList = [];
        },

        // Clear all diagnosis tracking (complete reset)
        clearAllDiagnosisData: (state) => {
            // Reset framework tracking
            state.completedElements = 0;
            state.totalElements = 0;
            state.completionRate = 0;
            state.frameworkCategoryStatusList = [];
            
            // Reset scoring
            state.scoringRubric = [];
            state.totalPointsEarned = 0;
            state.totalPointsPossible = 0;
            state.scoringCompletionPercentage = 0;
            state.hasScoring = false;
        },

        // Set framework category completion status
        setFrameworkCategoryStatus: (state, action: PayloadAction<{
            categoryName: string;
            isCompleted: boolean;
        }>) => {
            const { categoryName, isCompleted } = action.payload;
            
            const categoryIndex = state.frameworkCategoryStatusList.findIndex(
                item => item.category_name === categoryName
            );
            
            if (categoryIndex !== -1) {
                // Update existing category
                state.frameworkCategoryStatusList[categoryIndex].is_completed = isCompleted;
            } else {
                // Add new category
                state.frameworkCategoryStatusList.push({
                    category_name: categoryName,
                    is_completed: isCompleted
                });
            }
            
            // Recalculate completion stats
            state.completedElements = state.frameworkCategoryStatusList.filter(c => c.is_completed).length;
            state.totalElements = state.frameworkCategoryStatusList.length;
            state.completionRate = state.totalElements > 0 
                ? Math.round((state.completedElements / state.totalElements) * 100)
                : 0;
        },

        // Bulk update framework categories
        updateFrameworkCategories: (state, action: PayloadAction<{
            category_name: string;
            is_completed: boolean;
        }[]>) => {
            state.frameworkCategoryStatusList = action.payload;
            
            // Recalculate completion stats
            state.completedElements = action.payload.filter(c => c.is_completed).length;
            state.totalElements = action.payload.length;
            state.completionRate = state.totalElements > 0 
                ? Math.round((state.completedElements / state.totalElements) * 100)
                : 0;
        },
    }
});

export const { 
    // Framework completeness actions
    updateDiagnosisCompleteness, 
    clearDiagnosisCompleteness,
    setFrameworkCategoryStatus,
    updateFrameworkCategories,
    
    // Scoring actions
    initializeScoring,
    updateScoring,
    updateScoringCategory,
    addPointsToCategory,
    addMessageToCategory,
    updateCategoryInstructions,
    clearScoring,
    
    // General actions
    clearAllDiagnosisData
} = diagnosisSlice.actions;

export default diagnosisSlice.reducer;